import React from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';

import { bindActionCreators } from 'redux';

import Tag from 'antd/es/tag';

import { parseQuery } from '@common/typescript/utils/url';
import { BaseParams } from '@common/react/objects/BaseParams';
import Logout from '@common/react/components/UI/Logout/Logout';
import { getUserName } from '@common/react/utils/utils';
import ImageLazy from '@common/react/components/UI/ImageLazy/ImageLazy';
import TransmutationBack from '@common/react/components/Forms/TransmutationBack';
import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import Avatar from '@common/react/components/Forms/Avatar';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import GoogleMapLink from '@common/react/components/google/GoogleMapLink/GoogleMapLink';

import { ApplicationState } from '@app/store';
import { actionCreators } from '@app/store/HeaderSearch';
import DashboardTopAlert from '@app/components/UI/DashboardTopAlert/DashboardTopAlert';
import { SearchFilterState } from '@app/store/SearchFilter';
import ShoppingCartDropdown from '@app/components/UI/ShoppingCartDropdown/ShoppingCartDropdown';
import BurgerMenu from '@app/components/UI/Header/BurgerMenu';
import { useMenuStateProviderContext } from '@app/components/UI/Menu/MenuStateProvider';
import Search, { clearValues, emptyValues } from '@app/components/UI/Header/Search';
import { STORAGE_KEYS } from '@app/objects/StorageKeys';
import { User } from '@app/objects/User';

import '@app/scss/pages/headerSearch.scss';

const HeaderSearch: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		getTransmutationFlag,
		getUser,
	} = useApplicationContext();
	const transmuted = getTransmutationFlag();
	const user = getUser<User>();
	const state = useSelector((state: ApplicationState) => state.header, shallowEqual);
	const searchFilterData = useSelector((state: ApplicationState) => state.searchFilterData, shallowEqual);
	const dispatch = useDispatch();
	const { setState } = bindActionCreators(actionCreators, dispatch);
	const context = useMenuStateProviderContext();
	const { state: { mounted }, actions: { setState: setMenuState } } = context;
	const locationData = useSelector((state: ApplicationState) => state.buildData?.item?.location, shallowEqual);

	const userName = user ? (user.firstName || user.lastName ? getUserName(user) : user.email) : '';
	const [keys, setKeys] = React.useState<any>({ key: 'initial' });

	const [isOpen, setIsOpen] = React.useState<boolean>(false);

	const toggle = () => setIsOpen(!isOpen);

	const [values, setValues] = React.useState<any>({
		locationName: '',
		globalPayerName: '',
		text: '',
		languages: [],
		certificates: [],
	});

	const handleUrl = (arr: Array<{ name: string, value: any}>) => {
		const searchObj = location.pathname === '/doctors' ? parseQuery(location.search)
			: { ...state, text: undefined };

		arr.forEach(({ name, value }) => {
			searchObj[name] = value instanceof Array ? `[${value}]` : `${value}`;
		});

		const emptyValues = ['', 'null', 'undefined', undefined, null, '-1', '[]'];

		const search = Object.keys(searchObj)
			.filter((k) => emptyValues.indexOf(searchObj[k]) === -1)
			.map((k) => `${k}=${searchObj[k]}`).join('&');

		if (location.pathname !== '/doctors') {
			navigate(`/doctors?${search}`);
		} else {
			navigate(`${location.pathname.replace(/\/\d+/, '/1')}?${search}`, { replace: true });
		}
	};

	const onSelectSearch = (value, option) => {
		const obj = option.props.item;
		const update = (name, value) => {
			setState({
				...state,
				...emptyValues,
				[name]: value,
			});
			handleUrl([...clearValues, { name, value }]);
		};

		if (obj.doctor !== null) {
			setState({
				...state,
				...emptyValues,
			});
			navigate(`/doctors/${obj.doctor.portalPathEn}`);
		} else if (obj.specialty !== null && obj.specialty !== undefined) {
			update('specialtyIds', [obj.specialty.id]);
		} else if (obj.globalProcedure !== null) {
			update('globalProcedureIds', [obj.globalProcedure.id]);
		} else if (obj.clinic !== null) {
			setState({
				...state,
				...emptyValues,
			});
			navigate(`/clinics/${obj.clinic.portalPathEn}`);
		} else if (obj.insurance !== null) {
			update('globalPayerIds', [obj.insurance.id]);
		} else if (obj.special !== null) {
			setState({
				...state,
				...emptyValues,
			});
			navigate(`/specials/${obj.special.path}`);
		} else if (obj.location !== null) {
			setState({
				...state,
				...emptyValues,
			});
			navigate(`/locations/${obj.location.path}`);
		}
	};

	const handleSearchFilterResult = (searchObj: BaseParams, result: SearchFilterState) => {
		const text = searchObj.text;
		if (!text) {
			if (result.doctorName) {
				handleUrl([...clearValues, { name: 'doctorId', value: searchObj.doctorId }]);
			} else if (result.globalProcedureName) {
				handleUrl([...clearValues, { name: 'globalProcedureId', value: searchObj.globalProcedureId }]);
			} else if (result.specialtyName) {
				handleUrl([...clearValues, { name: 'specialtyId', value: searchObj.specialtyId }]);
			} else if (result.locationName) {
				handleUrl([...clearValues, { name: 'locationId', value: searchObj.locationId }]);
			}
		}

		setKeys((prev) => ({
			...prev,
			globalProcedureIds: Math.random(),
			specialtyIds: Math.random(),
		}));
		setState(searchObj);
	};

	React.useEffect(() => {
		let searchObj: BaseParams = {};

		if (location.pathname === '/doctors') {
			searchObj = getSearchParamsFromUrl(location);
		} else {
			searchObj = {
				doctorId: -1,
				specialtyId: -1,
				globalProcedureId: -1,
			};
		}

		if (searchObj.text) {
			handleUrl([
				...clearValues,
				{ name: 'text', value: searchObj.text },
			]);
			setValues((prev) => ({
				...prev,
				text: searchObj.text,
			}));
		}

		handleSearchFilterResult(searchObj, searchFilterData);
	}, []);

	React.useEffect(() => {
		let text = state.text;
		if (!text) {
			if (searchFilterData.doctorName) {
				text = `Doctor: ${searchFilterData.doctorName}`;
			} else if (searchFilterData.globalProcedureName) {
				text = `Procedure: ${searchFilterData.globalProcedureName}`;
			} else if (searchFilterData.specialtyName) {
				text = `Specialty: ${searchFilterData.specialtyName}`;
			} else if (searchFilterData.locationName) {
				text = `Location: ${searchFilterData.locationName}`;
			}
		}

		setValues((prev) => ({
			...prev,
			...searchFilterData,
			text: text || '',
		}));
	}, [searchFilterData]);

	React.useEffect(() => {
		// setIsOpen(false);
		setMenuState((prev) => ({ ...prev, open: false }));
		if (location.pathname !== '/doctors') {
			setState({});
		}
	}, [location.pathname]);

	return (
		<>
			<DashboardTopAlert />
			<div className="search-header">
				<div className={`search-page search-page-content pull-to-end ${isOpen ? 'search_open' : 'search_close'}`}>
					<div className="logo">
						<NavLink to="/">
							<ImageLazy src="/amp-assets/images/logo.png" alt="Northern Tuna Logo" height="72px" width="150px" />
						</NavLink>
					</div>
					<div className="row search">
						{locationData ? <GoogleMapLink className="search__location-tag" address={locationData?.addressEn}>
							<i className="fa fa-map-marker" />
&nbsp;
							{locationData.nameEn}
						</GoogleMapLink> : null}
						<div className="search-container col-sm-12">
							<Search
								key={keys.key}
								value={values.text || ''}
								onSelect={onSelectSearch}
								onChange={(value = '') => {
									setState({
										...state,
										text: value,
										...emptyValues,
									});
									setValues((prev) => ({
										...prev,
										text: value,
									}));
									handleUrl([
										...clearValues,
										{ name: 'text', value },
									]);
								}}
								initFocus={isOpen}
								onSearchClick={(e, value) => {
									if (!value) {
										setIsOpen(false);
									}
									setState({
										...state,
										text: value,
										...emptyValues,
									});
									setValues((prev) => ({
										...prev,
										text: value,
									}));
									handleUrl([
										...clearValues,
										{ name: 'text', value },
									]);
								}}
								onCloseClick={(e, value, setValue) => {
									setState({
										...state,
										text: '',
										...emptyValues,
									});
									handleUrl([
										...clearValues,
										{ name: 'text', value: '' },
									]);
									setKeys((prev) => ({ ...prev, key: Math.random() }));
									setValues((prev) => ({ ...prev, text: '' }));
									if (value) setValue('');
									if (!['doctorId', 'specialtyId', 'globalProcedureId', 'locationId', 'globalPayerId']
										.some((key) => state[key] && state[key] > 0) && !value) {
										setIsOpen(false);
									}
								}}
							/>
						</div>
						<div className="col-sm-12 bottom-menu__container mobile-hide">
							<div className="pull-left">
								<nav className="skew-menu">
									<ul>
										<li><NavLink className="" to="/doctors">Doctors</NavLink></li>
										<li><NavLink className="ml10" to="/procedures">Procedures</NavLink></li>
										<li><NavLink className="ml10" to="/specialties">Specialties</NavLink></li>
										<li><NavLink className="ml10" to="/locations">Locations</NavLink></li>
										<li><NavLink className="ml10" to="/clinics">Clinics</NavLink></li>
									</ul>
								</nav>
							</div>
						</div>
					</div>
					<div className="shopping-cart search-icon">
						<div className="shopping-cart__icon" onClick={() => toggle()}>
							<i className="fa fa-search" title="search" />
						</div>
					</div>
					<BurgerMenu getPopupContainer={(node) => node.closest('.search-page') || document.body} />
					<ShoppingCartDropdown />
					{user ? <div className="links-container">
						<div className="header-link mobile-hide btn btn-default">
							<NavLink to="/specials">Specials Shop</NavLink>
						</div>
						<div className="header-link mobile-hide btn btn-primary">
							<NavLink to="/doctors">Schedule an Appointment</NavLink>
						</div>
					</div> : null}
					{user
						? <div className="search-header-user-block">
							<ul className={`search-header-user-block__icons ${transmuted ? 'short' : ''}`}>
								{/*
							<li>
								<NavLink to="/profile"><i className="fa fa-user" title="Profile"/>
									{user && user.unviewedMessagesCount > 0 && <div className="badge search-header-user-block__badge">{user.unviewedMessagesCount}</div>}
								</NavLink>
							</li>
							*/}
								{/* <li title="Specials Shop" className="mobile-hide">
								<NavLink to="/specials">
									<i className="fa fa-cubes"/>
								</NavLink>
							</li>
							<li title="Schedule an Appointment" className="mobile-hide">
								<NavLink to="/doctors">
									<i className="fa fa-calendar-plus-o"/>
								</NavLink>
							</li>
							<li className="vertical-line hide-mobile"/> */}
								<li>
									<NavLink to="/chats">
										<i className="fa fa-comments" title="Chats" />
										{user && user.unviewedMessagesCount > 0 && (
											<div className="badge search-header-user-block__badge">
												{user.unviewedMessagesCount}
											</div>
										)}
									</NavLink>
								</li>
								<li>
									<NavLink to="/notifications">
										<i className="fa fa-bell" title="Notifications" />
										{user.unviewedNotificationsCount > 0 && (
											<div className="badge search-header-user-block__badge">
												{user.unviewedNotificationsCount}
											</div>
										)}
									</NavLink>
								</li>
								<TransmutationBack
									className="transmutation-button"
									onSuccess={() => localStorage.removeItem(STORAGE_KEYS.CHECKOUT_UID)}
									wrapper={(children) => <li>{children}</li>}
								/>
								<li className="mobile-hide">
									<Logout
										render={(logout) => <i onClick={logout} className="fa fa-power-off" title="Logout" />}
										clearState
									/>
								</li>
								<li className="avatar">
									{mounted
										? <a onClick={() => setMenuState((prev) => ({ ...prev, open: !prev.open }))}>
											<div className="search-header-avatar" title={userName}>
												<Avatar
													type=""
													readonly
													className="avatar-container"
													thumbClassName=""
													showDownload={false}
													showOpenIcon={false}
													withModal={false}
													object={user}
													color={user.color ?? undefined}
													firstName={user.firstName}
													lastName={user.lastName}
												/>
											</div>
										</a>
										: <NavLink to="/dashboard">
											<div className="search-header-avatar" title={userName}>
												<Avatar
													readonly
													type=""
													className="avatar-container"
													thumbClassName=""
													showDownload={false}
													showOpenIcon={false}
													withModal={false}
													object={user}
													color={user.color ?? undefined}
													firstName={user.firstName}
													lastName={user.lastName}
												/>
											</div>
										</NavLink>
									}
								</li>
							</ul>
						</div>
						: <div className="links-container">
							<div className="header-link mobile-hide btn btn-default">
								<NavLink to="/specials">Specials Shop</NavLink>
							</div>
							<div className="header-link mobile-hide btn btn-primary">
								<NavLink to="/doctors">Schedule an Appointment</NavLink>
							</div>
							<div className="header-link mobile-hide btn btn-default login">
								<NavLink to="/login" className="btn-link">Patient Login</NavLink>
							</div>
							<div className="search-header-user-block show-mobile show-mobile__block" style={{ padding: '0 6px' }}>
								<ul className="search-header-user-block__icons">
									<li>
										<NavLink to="/login"><i className="fa fa-user" /></NavLink>
									</li>
								</ul>
							</div>
						</div>
					}
					<div className="emr-link">
						<Tag color="orange">
							<NavLink to="/contact-support">
								Contact Us
							</NavLink>
						</Tag>
					&nbsp;
						<Tag color="orange">
							<a href="https://portal.northerntuna.com/" target="_blank" rel="noreferrer">
								EMR
							</a>
						</Tag>
					</div>
					<div className="line-1" />
				</div>
			</div>
		</>
	);
};

export default HeaderSearch;
